/**
 * Component Manager
 * Handles JS component lifecycle with automatic reinitialization for FacetWP and other AJAX patterns
 *
 * FRONTEND ONLY - Do not use in block editor
 */

class ComponentManager {
    constructor() {
        this.components = new Map();
        this.instances = new WeakMap();
        this.facetWPInitialized = false;
    }

    /**
     * Register a component
     * @param {string} selector - CSS selector for the component wrapper
     * @param {Object} handlers
     * @param {Function} handlers.init - Receives element, returns instance
     * @param {Function} [handlers.destroy] - Receives element and instance
     * @param {string} [handlers.trackSelector] - Inner selector to track for DOM replacement (for FacetWP)
     */
    register(selector, handlers) {
        this.components.set(selector, {
            init: handlers.init,
            destroy: handlers.destroy || (() => {}),
            trackSelector: handlers.trackSelector || null,
        });

        this.initAll(selector);
        this.setupFacetWP();

        return this;
    }

    initAll(selector) {
        const config = this.components.get(selector);
        if (!config) return;

        document.querySelectorAll(selector).forEach((el) => {
            this.initElement(el, config);
        });
    }

    initElement(el, config) {
        // Skip if already initialized
        if (this.instances.has(el)) return;

        try {
            const instance = config.init(el);
            if (instance) {
                // Store the tracked inner element if specified
                const trackedEl = config.trackSelector ? el.querySelector(config.trackSelector) : null;

                this.instances.set(el, { instance, trackedEl });
            }
        } catch (error) {
            console.error('Component init failed:', error, el);
        }
    }

    destroyElement(el, config) {
        const data = this.instances.get(el);
        if (!data) return;

        try {
            config.destroy(el, data.instance);
        } catch (error) {
            console.error('Component destroy failed:', error, el);
        }

        this.instances.delete(el);
    }

    /**
     * Check if a component needs reinitialization
     * Returns true if the tracked inner element is no longer in the DOM
     */
    needsReinit(el, config) {
        const data = this.instances.get(el);
        if (!data) return true;

        // If we're tracking an inner element, check if it's still connected
        if (data.trackedEl) {
            return !data.trackedEl.isConnected;
        }

        return false;
    }

    reinitAll() {
        this.components.forEach((config, selector) => {
            document.querySelectorAll(selector).forEach((el) => {
                if (this.needsReinit(el, config)) {
                    this.destroyElement(el, config);
                    this.initElement(el, config);
                }
            });
        });
    }

    setupFacetWP() {
        if (this.facetWPInitialized) return;
        this.facetWPInitialized = true;

        document.addEventListener('facetwp-loaded', () => {
            this.reinitAll();
        });
    }
}

const componentManager = new ComponentManager();

export function registerComponent(selector, handlers) {
    return componentManager.register(selector, handlers);
}

export function reinitComponents() {
    componentManager.reinitAll();
}
